import React, { useState, useEffect } from 'react';
import { Card, Tabs, Table, Upload, message, Row, Col, Statistic, Spin, Select, DatePicker, InputNumber, Button, Alert, Popconfirm, Space, Modal } from 'antd';
import SafetyPointsCalcPage from './SafetyPointsCalcPage';
import { UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../api/http';

const { TabPane } = Tabs;
const { Dragger } = Upload;

const HRDataPage = () => {
  // 状态管理
  const [activeTab, setActiveTab] = useState('1');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [attendanceData, setAttendanceData] = useState([]);
  const [uploadPreviewData, setUploadPreviewData] = useState([]);
  const [uploadPreviewVisible, setUploadPreviewVisible] = useState(false);
  const [uploadConfirmToken, setUploadConfirmToken] = useState('');
  const [confirmingImport, setConfirmingImport] = useState(false);
  const [batchOptions, setBatchOptions] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchLoading, setBatchLoading] = useState(false);
  // 安全绩效原始数据表格已移除，不再使用
  const [statistics, setStatistics] = useState({
    totalEmployees: 0,
    above16Days: 0,
    below16Days: 0,
    below16DaysNames: []
  });
  // 财务统计相关状态
  const defaultFinancialMonth = dayjs().date() <= 5 ? dayjs().subtract(1,'month') : dayjs();
  const [financialMonth, setFinancialMonth] = useState(defaultFinancialMonth);
  const [financialInput, setFinancialInput] = useState({ total_amount: null, tax_rate: null });
  const [financialStats, setFinancialStats] = useState({
    monthAmount: 0,
    monthTax: 0,
    ytdAmount: 0,
    ytdTax: 0,
    lastYearSameAmount: 0,
    lastYearSameTax: 0,
    allYearsAmount: 0,
    allYearsTax: 0,
    taxRate: 0
  });
  const [savingFinancial, setSavingFinancial] = useState(false);
  const [deletingFinancial, setDeletingFinancial] = useState(false);
  // 出勤日期范围（默认当月）
  const [dateRange, setDateRange] = useState([dayjs().startOf('month'), dayjs().endOf('month')]);
  const [userPerms, setUserPerms] = useState([]);

  // 计算出勤统计
  const calculateStatistics = (data) => {
    const totalEmployees = data.length;
    const above16Days = data.filter(item => item.attendance >= 16).length;
    const below16DaysArr = data.filter(item => item.attendance < 16);
    const below16DaysNames = below16DaysArr.map(item => item.name);
    setStatistics({
      totalEmployees,
      above16Days,
      below16Days: below16DaysArr.length,
      below16DaysNames
    });
  };
  
  // 获取出勤数据
  const fetchAttendanceData = async (range, batch = selectedBatch) => {
    setLoading(true);
    try {
      let response;
      if (batch) {
        response = await api.get('/hr-data/attendance/by-batch', { params: { import_batch: batch } });
      } else {
        response = await api.get('/hr-data/attendance', {
          params: {
            start_date: range[0].format('YYYY-MM-DD'),
            end_date: range[1].format('YYYY-MM-DD')
          }
        });
      }
      
      // 处理API返回的数据，确保字段名称匹配
      const processedData = response.data.map(item => ({
        id: item.id,
        seqNo: item.seq_no,
        process: item.process,
        name: item.name,
        team: item.team,
        position: item.position,
        departmentApproval: item.department_approval,
        remark: item.remark,
        attendance: item.attendance,
      }));
      
      setAttendanceData(processedData);
      // 计算统计数据（无数据则统计为0）
      calculateStatistics(processedData);
      
      // 批次模式下本地统计即可；非批次时仍可尝试后端统计接口
      if (!batch) {
        try {
          const statsResponse = await api.get('/hr-data/attendance/statistics', {
            params: {
              start_date: range[0].format('YYYY-MM-DD'),
              end_date: range[1].format('YYYY-MM-DD')
            }
          });
          if (statsResponse.data && typeof statsResponse.data === 'object') {
            setStatistics({
              totalEmployees: statsResponse.data.totalCount || 0,
              above16Days: statsResponse.data.above16Days || 0,
              below16Days: statsResponse.data.below16Days || 0,
              below16DaysNames: Array.isArray(statsResponse.data.below16Days) ? statsResponse.data.below16Days : []
            });
          }
        } catch (statsError) {
          console.error('获取出勤统计数据失败:', statsError);
        }
      }
    } catch (error) {
      message.error('获取出勤数据失败');
      console.error('获取出勤数据失败:', error);
      
      // 错误时不再展示模拟数据，保持为空避免误解
      setAttendanceData([]);
      calculateStatistics([]);
    } finally {
      setLoading(false);
    }
  };
  
  // 已移除安全绩效部门级表格逻辑
  
  // 处理文件上传（增强debug和异常提示）
  const handleUpload = async (file, onSuccess, onError) => {
    console.log('[HRData] handleUpload触发: raw file object ->', file);
    try {
      if (!file) {
        const error = new Error('未选择文件');
        message.error(error.message);
        console.error('[HRData] 未选择文件 (handleUpload)');
        if (onError) onError(error);
        return;
      }
      if (!dateRange || dateRange.length < 2) {
        const error = new Error('日期范围未设置');
        message.error(error.message);
        console.error('[HRData] 日期范围未设置 (handleUpload)');
        if (onError) onError(error);
        return;
      }
      // 检查文件类型
      const isExcel = file.type === 'application/vnd.ms-excel' || 
                     file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                     file.name.endsWith('.xlsx') ||
                     file.name.endsWith('.xls');
      if (!isExcel) {
        const error = new Error('请上传Excel文件！');
        message.error(error.message);
        console.error('[HRData] 文件类型不正确:', file.type, file.name);
        if (onError) onError(error);
        return;
      }
  setUploading(true);
  const formData = new FormData();
  formData.append('file', file);
  const startDateStr = dateRange[0].format('YYYY-MM-DD');
  const endDateStr = dateRange[1].format('YYYY-MM-DD');
  formData.append('start_date', startDateStr);
  formData.append('end_date', endDateStr);
  // 自动生成批次（如2025-10）
  const batch = dateRange[0].format('YYYY-MM');
  formData.append('import_batch', batch);
  formData.append('preview_only', '1');
      console.log('[HRData] upload form payload =>', { startDateStr, endDateStr, batch, fileName: file.name, size: file.size, type: file.type });
      console.log('[HRData] 开始调用上传API...');
      const response = await api.post('/hr-data/attendance/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log('[HRData] 上传进度:', percentCompleted, '%');
        }
      });
      console.log('[HRData] 上传成功:', response.data);
      message.success('解析成功，请确认导入');
      if (Array.isArray(response.data?.preview_rows)) {
        const previewData = response.data.preview_rows.map((item, idx) => ({
          id: `preview-${idx + 1}`,
          seqNo: item.seq_no,
          process: item.process,
          name: item.name,
          team: item.team,
          position: item.position,
          departmentApproval: item.department_approval,
          remark: item.remark,
          attendance: item.attendance,
        }));
        setUploadPreviewData(previewData);
        setUploadConfirmToken(response.data?.confirm_token || '');
        setUploadPreviewVisible(true);
      } else {
        setUploadPreviewData([]);
        setUploadConfirmToken('');
      }
      if (onSuccess) onSuccess(response.data);
    } catch (error) {
      message.error('文件上传失败: ' + (error.response?.data?.error || error.message));
      console.error('[HRData] 文件上传失败, stack:', error.stack || error);
      if (onError) onError(error);
    } finally {
      setUploading(false);
      console.log('[HRData] 上传流程结束');
    }
  };
  
  // 出勤数据表格列定义
  const attendanceColumns = [
    {
      title: '序号',
      dataIndex: 'seqNo',
      key: 'seqNo',
      width: 90,
    },
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '科室/工序',
      dataIndex: 'process',
      key: 'process',
    },
    {
      title: '班组',
      dataIndex: 'team',
      key: 'team',
    },
    {
      title: '岗位',
      dataIndex: 'position',
      key: 'position',
    },
    {
      title: '部门审定',
      dataIndex: 'departmentApproval',
      key: 'departmentApproval',
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
    },
    {
      title: '出勤',
      dataIndex: 'attendance',
      key: 'attendance',
      width: 100,
    },
  ];
  
  // 历史月份批量录入状态与方法
  const [selectedYear, setSelectedYear] = useState(dayjs().year());
  const [yearFinancial, setYearFinancial] = useState([]); // [{month:'01', total_amount, tax_rate, tax_due, saving:false}]
  const [yearLoading, setYearLoading] = useState(false);

  const buildYearSkeleton = (yearRecords) => {
    return (yearRecords || []).map(r => ({
      key: r.stat_month,
      month: r.stat_month,
      month_amount: parseFloat(r.total_amount),
      month_tax: parseFloat(r.tax_due),
      ytd_amount: r.ytd_amount != null ? parseFloat(r.ytd_amount) : null,
      ytd_tax: r.ytd_tax != null ? parseFloat(r.ytd_tax) : null,
      last_year_same_amount: r.last_year_same_amount != null ? parseFloat(r.last_year_same_amount) : null,
      last_year_same_tax: r.last_year_same_tax != null ? parseFloat(r.last_year_same_tax) : null,
      all_years_amount: r.all_years_amount != null ? parseFloat(r.all_years_amount) : null,
      all_years_tax: r.all_years_tax != null ? parseFloat(r.all_years_tax) : null
    }));
  };

  const fetchYearFinancial = async (year = selectedYear) => {
    setYearLoading(true);
    try {
      const res = await api.get('/hr-data/safety-financial/year', { params: { year } });
      setYearFinancial(buildYearSkeleton(res.data.records || []));
    } catch (e) {
      console.error('获取年份财务记录失败:', e);
      setYearFinancial(buildYearSkeleton([]));
    } finally {
      setYearLoading(false);
    }
  };

  // 历史列表不再单月编辑保存，统一通过上方保存入口
  const saveYearMonth = async () => {};
  
  // 初始化数据
  const fetchBatchOptions = async () => {
    setBatchLoading(true);
    try {
      const res = await api.get('/hr-data/attendance/batches');
      const batches = res.data?.batches || [];
      setBatchOptions(batches.map(b => ({ label: `${b.import_batch} (${b.count})`, value: b.import_batch })));
      if (!selectedBatch && batches.length) {
        const first = batches[0].import_batch;
        setSelectedBatch(first);
        // 立即按批次加载数据，避免先展示空+占位
        fetchAttendanceData(dateRange, first);
      }
    } catch (e) {
      console.error('获取批次列表失败:', e);
    } finally {
      setBatchLoading(false);
    }
  };

  const handleDeleteBatch = async (batch) => {
    if (!batch) return;
    try {
      await api.delete('/hr-data/attendance/batch', { params: { import_batch: batch } });
      message.success(`批次 ${batch} 已删除`);
      if (selectedBatch === batch) {
        setSelectedBatch(null);
      }
      await fetchBatchOptions();
      await fetchAttendanceData(dateRange, null);
    } catch (e) {
      message.error('删除批次失败: ' + (e.response?.data?.error || e.message));
    }
  };

  const handleConfirmImport = async () => {
    if (!uploadConfirmToken) {
      message.error('确认令牌缺失，请重新上传解析');
      return;
    }
    setConfirmingImport(true);
    try {
      const res = await api.post('/hr-data/attendance/upload/confirm', { confirm_token: uploadConfirmToken });
      message.success(`导入成功，共 ${res.data?.count || 0} 行`);
      setUploadPreviewVisible(false);
      setUploadConfirmToken('');

      await fetchBatchOptions();
      if (res.data?.batch) {
        setSelectedBatch(res.data.batch);
        await fetchAttendanceData(dateRange, res.data.batch);
      } else {
        await fetchAttendanceData(dateRange);
      }
    } catch (e) {
      message.error('确认导入失败: ' + (e.response?.data?.error || e.message));
    } finally {
      setConfirmingImport(false);
    }
  };

  const handleCancelPreview = () => {
    setUploadPreviewVisible(false);
    setUploadConfirmToken('');
  };

  useEffect(() => {
    // 仅加载批次；有批次时在内部触发加载，避免并发造成统计被覆盖
    fetchBatchOptions();
    // 尝试从本地缓存获取登录用户权限（登录成功时应写入 localStorage.user）
    try {
      const cachedUser = localStorage.getItem('user');
      if (cachedUser) {
        const parsed = JSON.parse(cachedUser);
        if (Array.isArray(parsed.permissions)) setUserPerms(parsed.permissions);
      }
    } catch(e){ /* ignore */ }
  }, []);
  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key === '2') {
      fetchFinancialStats(financialMonth.format('YYYY-MM'));
      fetchYearFinancial(selectedYear);
    }
  };

  // 获取财务统计
  const fetchFinancialStats = async (monthStr) => {
    try {
      const res = await api.get('/hr-data/safety-financial/stats', { params: { month: monthStr } });
      setFinancialStats({
        monthAmount: res.data?.monthAmount || 0,
        monthTax: res.data?.monthTax || 0,
        ytdAmount: res.data?.ytdAmount || 0,
        ytdTax: res.data?.ytdTax || 0,
        lastYearSameAmount: res.data?.lastYearSameAmount || 0,
        lastYearSameTax: res.data?.lastYearSameTax || 0,
        allYearsAmount: res.data?.allYearsAmount || 0,
        allYearsTax: res.data?.allYearsTax || 0,
        taxRate: res.data?.taxRate || 0
      });
      // 如果已有记录，预填输入
      if (res.data?.monthAmount) {
        setFinancialInput({ total_amount: res.data.monthAmount, tax_rate: res.data.taxRate });
      }
    } catch(e) {
      console.error('获取财务统计失败:', e);
    }
  };

  // 保存财务记录
  const saveFinancialRecord = async () => {
    const monthStr = financialMonth.format('YYYY-MM');
    if (financialInput.total_amount == null || financialInput.tax_rate == null) {
      message.error('请输入结算总价与税率');
      return;
    }
    setSavingFinancial(true);
    try {
      const res = await api.post('/hr-data/safety-financial/record', {
        month: monthStr,
        total_amount: financialInput.total_amount,
        tax_rate: financialInput.tax_rate
      });
      message.success('保存成功');
      await fetchFinancialStats(monthStr); // 自动刷新顶部统计
      await fetchYearFinancial(selectedYear); // 刷新年份列表
    } catch(e) {
      message.error('保存失败: ' + (e.response?.data?.error || e.message));
    } finally {
      setSavingFinancial(false);
    }
  };
  
  return (
    <div className="hr-data-page">
      <Card title="人力数据">
  <Tabs activeKey={activeTab} onChange={handleTabChange}>
          {/* 出勤数据标签页 */}
          <TabPane tab="出勤数据" key="1">
            {/* 统计卡片 */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={6}>
                <Card>
                  <Statistic title="总人数" value={statistics.totalEmployees} />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic title="出勤16天以上" value={statistics.above16Days} />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic title="出勤不足16天" value={statistics.below16Days} />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic title="出勤不足16天人员" value={statistics.below16DaysNames.length} />
                </Card>
              </Col>
            </Row>
            
            {/* 批次选择与文件上传区域 */}
            <Card style={{ marginBottom: 24 }}>
              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                <Space wrap align="start">
                  <div style={{ minWidth:240 }}>
                    <div style={{ fontSize:12, color:'#555', marginBottom:4 }}>选择批次</div>
                    <Space>
                      <Select
                        placeholder="选择导入批次"
                        loading={batchLoading}
                        value={selectedBatch}
                        options={batchOptions}
                        onChange={(val) => { setSelectedBatch(val || null); fetchAttendanceData(dateRange, val || null); }}
                        allowClear
                        style={{ width:200 }}
                      />
                      {selectedBatch && <Button size="small" onClick={()=>{ setSelectedBatch(null); fetchAttendanceData(dateRange, null); }}>使用日期范围</Button>}
                      {selectedBatch && userPerms.includes('hr_data_attendance_import') && (
                        <Popconfirm
                          title={`确认删除批次 ${selectedBatch} 吗？`}
                          description="删除后不可恢复"
                          onConfirm={() => handleDeleteBatch(selectedBatch)}
                          okText="删除"
                          cancelText="取消"
                        >
                          <Button size="small" danger>删除批次</Button>
                        </Popconfirm>
                      )}
                    </Space>
                    {selectedBatch && (
                      <div style={{ fontSize:11, color:'#888', marginTop:4 }}>当前批次行数: {attendanceData.length}</div>
                    )}
                  </div>
                  {!selectedBatch && (
                    <div style={{ minWidth:350 }}>
                      <div style={{ fontSize:12, color:'#555', marginBottom:4 }}>日期范围模式</div>
                      <Space>
                        <DatePicker.RangePicker
                          value={dateRange}
                          onChange={(r)=>{ if(r){ setDateRange(r); } }}
                        />
                        <Button size="small" onClick={()=> fetchAttendanceData(dateRange, null)}>查询</Button>
                      </Space>
                    </div>
                  )}
                  {userPerms.includes('hr_data_attendance_import') && (
                    <div style={{ minWidth:300, flex:1 }}>
                      <div style={{ fontSize:12, color:'#555', marginBottom:4 }}>上传新的考勤Excel（自动生成当月批次）</div>
                      <Dragger
                        style={{ padding:'6px 10px', borderRadius:4 }}
                        beforeUpload={(file) => {
                          handleUpload(
                            file,
                            () => {},
                            (err) => { message.error('文件上传失败: ' + (err?.response?.data?.error || err?.message)); }
                          );
                          return false;
                        }}
                        showUploadList={false}
                        multiple={false}
                        disabled={uploading}
                      >
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <UploadOutlined style={{ fontSize:16 }} />
                          <span style={{ fontSize:12 }}>上传考勤Excel</span>
                        </div>
                        <div style={{ fontSize:11, color:'#666', marginTop:4 }}>
                          列需包含：序号/姓名/科室/工序/班组/岗位/初评/复评/部门审定/备注/出勤
                        </div>
                      </Dragger>
                    </div>
                  )}
                </Space>
              </div>
            </Card>

            <Modal
              title={`上传解析预览（前${uploadPreviewData.length}行）`}
              open={uploadPreviewVisible}
              onOk={handleConfirmImport}
              okText="确认导入"
              cancelText="取消"
              onCancel={handleCancelPreview}
              confirmLoading={confirmingImport}
              width={1000}
              destroyOnClose
            >
              <Table
                columns={attendanceColumns}
                dataSource={uploadPreviewData}
                rowKey="id"
                pagination={{ pageSize: 8 }}
                size="small"
              />
            </Modal>
            
            {/* 出勤数据表格 */}
            <Spin spinning={loading}>
              <Table
                columns={attendanceColumns}
                dataSource={attendanceData}
                rowKey="id"
                pagination={{ pageSize: 10 }}
                locale={{ emptyText: '暂无出勤数据' }}
              />
            </Spin>

            
            {/* 出勤不足16天人员列表 */}
            {statistics.below16Days > 0 && (
              <Card title="出勤不足16天人员" style={{ marginTop: 24 }}>
                <p>{statistics.below16DaysNames.join('、')}</p>
              </Card>
            )}
          </TabPane>
          
          {/* 安全绩效财务标签页（仅使用 hr_safety_financial 表） */}
          <TabPane tab="安全绩效财务" key="2">
            <Card style={{ marginTop:24 }} title="安全绩效财务统计" extra={userPerms.includes('hr_data_financial_manage') && <Button size="small" onClick={async ()=>{ try { await api.post('/hr-data/safety-financial/recalc'); message.success('重算完成'); fetchFinancialStats(financialMonth.format('YYYY-MM')); fetchYearFinancial(selectedYear);} catch(e){ message.error('重算失败'); }} }>重算聚合</Button>}>
              {dayjs().date() <= 5 && (
                <Alert style={{ marginBottom:12 }} type="info" showIcon message={`当前日期为${dayjs().format('YYYY-MM-DD')}，请在每月5日前录入上个月(${dayjs().subtract(1,'month').format('YYYY-MM')})的结算数据。`} />
              )}
              <Row gutter={16} style={{ marginBottom:16 }}>
                <Col span={6}>
                  <DatePicker
                    picker="month"
                    value={financialMonth}
                    onChange={(val) => { if(val){ setFinancialMonth(val); fetchFinancialStats(val.format('YYYY-MM')); } }}
                    style={{ width:'100%' }}
                  />
                </Col>
                <Col span={6}>
                  <InputNumber
                    placeholder="当月结算总价"
                    value={financialInput.total_amount}
                    onChange={(v)=> setFinancialInput(prev=>({ ...prev, total_amount: v }))}
                    style={{ width:'100%' }}
                    min={0}
                    precision={2}
                    disabled={!userPerms.includes('hr_data_financial_manage')}
                  />
                </Col>
                <Col span={6}>
                  <InputNumber
                    placeholder="税率(如0.13)"
                    value={financialInput.tax_rate}
                    onChange={(v)=> setFinancialInput(prev=>({ ...prev, tax_rate: v }))}
                    style={{ width:'100%' }}
                    min={0}
                    max={1}
                    step={0.001}
                    precision={4}
                    disabled={!userPerms.includes('hr_data_financial_manage')}
                  />
                </Col>
                {userPerms.includes('hr_data_financial_manage') && (
                  <Col span={6} style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <Button type="primary" loading={savingFinancial} onClick={saveFinancialRecord}>保存/更新</Button>
                    <Popconfirm
                      title="确认删除该月份?"
                      description={`删除 ${financialMonth.format('YYYY-MM')} 并重算聚合`}
                      okText="删除"
                      cancelText="取消"
                      onConfirm={async ()=>{
                        setDeletingFinancial(true);
                        try {
                          await api.delete('/hr-data/safety-financial/record', { params:{ month: financialMonth.format('YYYY-MM') }});
                          message.success('删除成功并已重算');
                          await fetchFinancialStats(financialMonth.format('YYYY-MM'));
                          await fetchYearFinancial(selectedYear);
                        } catch(e){ message.error('删除失败'); } finally { setDeletingFinancial(false);} }}
                    >
                      <Button danger loading={deletingFinancial}>删除当前月</Button>
                    </Popconfirm>
                  </Col>
                )}
              </Row>
              <Row gutter={16}>
                <Col span={6}><Statistic title="单月金额" value={financialStats.monthAmount} precision={2} /></Col>
                <Col span={6}><Statistic title="单月税额" value={financialStats.monthTax} precision={2} /></Col>
                <Col span={6}><Statistic title="当年累计金额" value={financialStats.ytdAmount} precision={2} /></Col>
                <Col span={6}><Statistic title="当年累计税额" value={financialStats.ytdTax} precision={2} /></Col>
              </Row>
              <Row gutter={16} style={{ marginTop:16 }}>
                <Col span={6}><Statistic title="上年同期金额" value={financialStats.lastYearSameAmount} precision={2} /></Col>
                <Col span={6}><Statistic title="上年同期税额" value={financialStats.lastYearSameTax} precision={2} /></Col>
                <Col span={6}><Statistic title="历年累计金额" value={financialStats.allYearsAmount} precision={2} /></Col>
                <Col span={6}><Statistic title="历年累计税额" value={financialStats.allYearsTax} precision={2} /></Col>
              </Row>
            </Card>
            <Card style={{ marginTop:24 }} title="历史月份指标列表">
              <Row gutter={12} style={{ marginBottom:12 }}>
                <Col span={6}>
                  <DatePicker
                    picker="year"
                    value={dayjs(`${selectedYear}-01-01`)}
                    onChange={val => { if(val){ const y = val.year(); setSelectedYear(y); fetchYearFinancial(y); } }}
                    style={{ width:'100%' }}
                  />
                </Col>
                <Col span={18} style={{ display:'flex', alignItems:'center' }}>
                  <span style={{ fontSize:12, color:'#666' }}>显示该年份各月财务指标（单月金额/税额、当年累计、上年同期、历年累计）。录入或更新请在上方选择月份保存。</span>
                </Col>
              </Row>
              <Spin spinning={yearLoading}>
                <Table
                  dataSource={yearFinancial}
                  pagination={false}
                  size="small"
                  rowKey="month"
                  columns={[
                    { title:'月份', dataIndex:'month', width:100 },
                    { title:'单月金额', dataIndex:'month_amount', render: v => v==null? '-' : v.toFixed(2) },
                    { title:'单月税额', dataIndex:'month_tax', render: v => v==null? '-' : v.toFixed(2) },
                    { title:'当年累计金额', dataIndex:'ytd_amount', render: v => v==null? '-' : v.toFixed(2) },
                    { title:'当年累计税额', dataIndex:'ytd_tax', render: v => v==null? '-' : v.toFixed(2) },
                    { title:'上年同期金额', dataIndex:'last_year_same_amount', render: v => v==null? '-' : v.toFixed(2) },
                    { title:'上年同期税额', dataIndex:'last_year_same_tax', render: v => v==null? '-' : v.toFixed(2) },
                    { title:'历年累计金额', dataIndex:'all_years_amount', render: v => v==null? '-' : v.toFixed(2) },
                    { title:'历年累计税额', dataIndex:'all_years_tax', render: v => v==null? '-' : v.toFixed(2) }
                  ]}
                  scroll={{ x: 1100 }}
                />
              </Spin>
            </Card>
          </TabPane>
          { (userPerms.includes('safety_points_view') || userPerms.includes('safety_points_manage')) && (
            <TabPane tab="安全积分汇算" key="3">
              <SafetyPointsCalcPage permissions={userPerms} />
            </TabPane>
          )}
          { (userPerms.includes('safety_points_view') || userPerms.includes('safety_points_manage')) && (
            <TabPane tab="安全积分管理" key="4">
              <Alert type="info" showIcon message="安全积分管理占位：后续支持历史批次查询、导出、调整日志。" />
            </TabPane>
          )}
        </Tabs>
      </Card>
    </div>
  );
};

export default HRDataPage;